import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  RANKED_ENTRY_FEE,
  WHALE_RANKED_ENTRY_FEE,
  RANKED_DUOS_ENTRY_FEE,
  PRIZE_DISTRIBUTION,
  WHALE_PRIZE_DISTRIBUTION,
  PrizeDistribution
} from "../../../../../config"
import "./wiki-prizes.css"

type ModeKey = "ranked" | "whale_ranked" | "ranked_duos"

const MODE_CONFIG: Record<
  ModeKey,
  {
    labelKey: string
    entryFee: number
    distribution: { [playerCount: number]: PrizeDistribution }
    isDuos: boolean
  }
> = {
  ranked: {
    labelKey: "game_modes.RANKED",
    entryFee: RANKED_ENTRY_FEE,
    distribution: PRIZE_DISTRIBUTION,
    isDuos: false
  },
  whale_ranked: {
    labelKey: "game_modes.WHALE_RANKED",
    entryFee: WHALE_RANKED_ENTRY_FEE,
    distribution: WHALE_PRIZE_DISTRIBUTION,
    isDuos: false
  },
  ranked_duos: {
    labelKey: "game_modes.RANKED_DUOS",
    entryFee: RANKED_DUOS_ENTRY_FEE,
    distribution: PRIZE_DISTRIBUTION,
    isDuos: true
  }
}

export default function WikiPrizes() {
  const { t } = useTranslation()
  const [selectedMode, setSelectedMode] = useState<ModeKey>("ranked")
  const config = MODE_CONFIG[selectedMode]
  const playerCounts = Object.keys(config.distribution)
    .map(Number)
    .sort((a, b) => b - a)

  return (
    <div className="wiki-prizes">
      <h2>{t("prizes_and_fees")}</h2>
      <p className="wiki-prizes-description">{t("prizes_description")}</p>

      <div className="wiki-prizes-mode-select">
        {(Object.keys(MODE_CONFIG) as ModeKey[]).map((mode) => (
          <button
            key={mode}
            className={`bubbly ${selectedMode === mode ? "green" : "blue"}`}
            onClick={() => setSelectedMode(mode)}
          >
            {t(MODE_CONFIG[mode].labelKey)}
          </button>
        ))}
      </div>

      <p className="wiki-prizes-entry-fee">
        {t("entry_fee_label")}: <strong>${config.entryFee} USDC</strong>
        {config.isDuos && (
          <span className="wiki-prizes-note"> ({t("per_player")})</span>
        )}
      </p>

      {config.isDuos && (
        <p className="wiki-prizes-duos-note">
          {t("duos_prize_explanation")}
        </p>
      )}

      {playerCounts.map((playerCount) => {
        const dist = config.distribution[playerCount]
        if (!dist) return null
        const ranks = Object.keys(dist)
          .filter((k) => k !== "fee")
          .map(Number)
          .sort((a, b) => a - b)
        const totalPool = playerCount * config.entryFee
        const totalPrizes = ranks.reduce((sum, r) => sum + (dist[r] || 0), 0)

        return (
          <div key={playerCount} className="wiki-prizes-table-section">
            <h3>
              {t("players_in_game", { count: playerCount })}
            </h3>
            <p className="wiki-prizes-pool">
              {t("total_prize_pool")}: <strong>${totalPool.toFixed(2)}</strong>
              {" | "}
              {t("fee_to_house")}: <strong>${dist.fee.toFixed(2)}</strong>
              {" | "}
              {t("paid_out")}: <strong>${totalPrizes.toFixed(2)}</strong>
            </p>
            <table className="wiki-prizes-table">
              <thead>
                <tr>
                  <th>{t("rank")}</th>
                  <th>{t("prize_usdc")}</th>
                  <th>{t("net_result")}</th>
                </tr>
              </thead>
              <tbody>
                {ranks.map((rank) => {
                  const prize = dist[rank] || 0
                  const net = prize - config.entryFee
                  return (
                    <tr
                      key={rank}
                      className={
                        net > 0
                          ? "wiki-prizes-profit"
                          : net === 0
                            ? "wiki-prizes-even"
                            : "wiki-prizes-loss"
                      }
                    >
                      <td>#{rank}</td>
                      <td>${prize.toFixed(2)}</td>
                      <td>
                        {net > 0 ? "+" : ""}
                        {net.toFixed(2)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )
      })}
    </div>
  )
}
